import React, {
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  FC,
} from "react";

type VoiceWaveVisualizerProps = {
  isRecording: boolean;
  isPaused: boolean;
  width?: number | string;
  height?: number | string;
};

type ExtendedWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

const DEFAULT_WIDTH = 250;
const DEFAULT_HEIGHT = 60;
const DEFAULT_WRAPPER_WIDTH = "100%";
const MIN_CANVAS_WIDTH = 220;
const EDGE_PADDING_PX = 20;
const BASE_PATTERN_WIDTH = 300;

const opts = {
  smoothing: 0.85,
  fft: 8,
  minDecibels: -70,
  scale: 0,
  glow: 50,
  color1: [124, 58, 237],
  color2: [79, 70, 229],
  color3: [192, 38, 211],
  fillOpacity: 0.35,
  lineWidth: 0,
  blend: "lighter",
  shift: 35,
  width: 50,
  amp: 0.4,
} as const;

const shuffle = [1, 3, 0, 4, 2];

interface FixedBufferUint8Array extends Uint8Array {
  buffer: ArrayBuffer;
  subarray(begin?: number, end?: number): FixedBufferUint8Array;
  slice(begin?: number, end?: number): FixedBufferUint8Array;
}

type FrequencyArray = FixedBufferUint8Array;

const range = (i: number) => Array.from(Array(i).keys());

const scaleValue = (i: number) => {
  const x = Math.abs(2 - i);
  const s = 3 - x;
  return (s / 3) * opts.amp;
};

const frequencyAt = (freqs: FrequencyArray, channel: number, index: number) => {
  const band = 2 * channel + shuffle[index] * 6;
  return freqs[band] ?? 0;
};

const drawPath = (
  ctx: CanvasRenderingContext2D,
  channel: number,
  canvasWidth: number,
  freqs: FrequencyArray
) => {
  const color = opts[`color${channel + 1}` as keyof typeof opts] as Readonly<
    [number, number, number]
  >;

  const availableWidth = Math.max(
    MIN_CANVAS_WIDTH,
    canvasWidth - EDGE_PADDING_PX * 2
  );
  const patternWidth = Math.min(
    availableWidth,
    Math.max(MIN_CANVAS_WIDTH, canvasWidth * 0.75)
  );
  const patternLeft = (canvasWidth - patternWidth) / 2;
  const widthScale = patternWidth / BASE_PATTERN_WIDTH;

  const gradient = ctx.createLinearGradient(
    patternLeft,
    0,
    patternLeft + patternWidth,
    0
  );
  gradient.addColorStop(0, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0)`);
  gradient.addColorStop(
    0.2,
    `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opts.fillOpacity})`
  );
  gradient.addColorStop(
    0.8,
    `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opts.fillOpacity})`
  );
  gradient.addColorStop(1, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0)`);

  ctx.fillStyle = gradient;
  ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.25)`;
  ctx.lineWidth = opts.lineWidth;
  ctx.shadowBlur = Math.min(Math.max(patternWidth * 0.02, 2), 10);
  ctx.globalCompositeOperation = opts.blend as GlobalCompositeOperation;

  const m = DEFAULT_HEIGHT / 2;
  const baseOffset = (BASE_PATTERN_WIDTH - 15 * opts.width) / 2;
  const x = range(15).map(
    (i) =>
      patternLeft +
      (baseOffset + channel * opts.shift + i * opts.width) * widthScale
  );
  const y = range(5).map((i) =>
    Math.max(0, m - scaleValue(i) * frequencyAt(freqs, channel, i))
  );
  const h = 2 * m;

  ctx.beginPath();
  ctx.moveTo(0, m);
  ctx.lineTo(x[0], m + 1);

  ctx.bezierCurveTo(x[1], m + 1, x[2], y[0], x[3], y[0]);
  ctx.bezierCurveTo(x[4], y[0], x[4], y[1], x[5], y[1]);
  ctx.bezierCurveTo(x[6], y[1], x[6], y[2], x[7], y[2]);
  ctx.bezierCurveTo(x[8], y[2], x[8], y[3], x[9], y[3]);
  ctx.bezierCurveTo(x[10], y[3], x[10], y[4], x[11], y[4]);

  ctx.bezierCurveTo(x[12], y[4], x[12], m, x[13], m);
  ctx.lineTo(canvasWidth, m + 1);
  ctx.lineTo(x[13], m - 1);

  ctx.bezierCurveTo(x[12], m, x[12], h - y[4], x[11], h - y[4]);
  ctx.bezierCurveTo(x[10], h - y[4], x[10], h - y[3], x[9], h - y[3]);
  ctx.bezierCurveTo(x[8], h - y[3], x[8], h - y[2], x[7], h - y[2]);
  ctx.bezierCurveTo(x[6], h - y[2], x[6], h - y[1], x[5], h - y[1]);
  ctx.bezierCurveTo(x[4], h - y[1], x[4], h - y[0], x[3], h - y[0]);
  ctx.bezierCurveTo(x[2], h - y[0], x[1], m, x[0], m);

  ctx.lineTo(0, m);
  ctx.fill();
};

const VoiceWaveVisualizer: FC<VoiceWaveVisualizerProps> = ({
  isRecording,
  isPaused,
  width,
  height,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const freqsRef = useRef<FrequencyArray | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isPausedRef = useRef(isPaused);
  const resizeObsRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const resizeCanvasToContainer = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return 0;
    }

    const rawWidth =
      typeof width === "number" ? width : container.clientWidth || DEFAULT_WIDTH;
    const cssWidth = Math.max(
      MIN_CANVAS_WIDTH,
      rawWidth - EDGE_PADDING_PX * 2
    );
    const cssHeight =
      typeof height === "number" ? height : DEFAULT_HEIGHT;

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    return cssWidth;
  }, [height, width]);

  const visualize = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    const freqs = freqsRef.current;
    const container = containerRef.current;
    if (!canvas || !analyser || !freqs || !container) {
      animationFrameRef.current = requestAnimationFrame(visualize);
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      animationFrameRef.current = requestAnimationFrame(visualize);
      return;
    }

    const canvasWidthCSS = resizeCanvasToContainer();
    if (canvasWidthCSS === 0) {
      animationFrameRef.current = requestAnimationFrame(visualize);
      return;
    }

    analyser.smoothingTimeConstant = opts.smoothing;
    analyser.fftSize = Math.pow(2, opts.fft);
    analyser.minDecibels = opts.minDecibels;
    analyser.maxDecibels = 0;

    analyser.getByteFrequencyData(freqs);

    const clearHeight =
      typeof height === "number" ? height : DEFAULT_HEIGHT;
    ctx.clearRect(0, 0, canvasWidthCSS, clearHeight);

    const currentlyPaused = isPausedRef.current;

    const hasAudio = freqs.some((value) => value > 0);
    if (currentlyPaused || !hasAudio) {
      // keep canvas clean when muted or no sound
      ctx.clearRect(0, 0, canvasWidthCSS, clearHeight);
      animationFrameRef.current = requestAnimationFrame(visualize);
      return;
    } else {
      for (let channel = 0; channel < 3; channel += 1) {
        drawPath(ctx, channel, canvasWidthCSS, freqs);
      }
    }

    animationFrameRef.current = requestAnimationFrame(visualize);
  }, [height, resizeCanvasToContainer]);

  const startVisualizer = useCallback(async () => {
    if (initializedRef.current) return;
    if (!canvasRef.current || !containerRef.current) return;

    initializedRef.current = true;

    try {
      const AudioContextCtor =
        window.AudioContext ||
        (window as ExtendedWindow).webkitAudioContext ||
        null;

      if (!AudioContextCtor) {
        throw new Error("AudioContext is not supported in this browser");
      }

      const audioContext = new AudioContextCtor();
      const analyser = audioContext.createAnalyser();
      const freqs = new Uint8Array(
        analyser.frequencyBinCount
      ) as FrequencyArray;

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      freqsRef.current = freqs;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const input = audioContext.createMediaStreamSource(stream);
      input.connect(analyser);

      // kick off visualize loop
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(visualize);
      }
    } catch (err) {
      console.error("Error accessing microphone:", err);
      initializedRef.current = false;
    }
  }, [visualize]);

  // Handle starting/stopping based on isRecording
  useEffect(() => {
    if (isRecording) {
      // start visualizer
      startVisualizer();
    } else {
      // stop visualizer & cleanup audio
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch((error) => {
          console.error("Error closing audio context:", error);
        });
        audioContextRef.current = null;
      }

      analyserRef.current = null;
      freqsRef.current = null;
      initializedRef.current = false;
    }

    return () => {
      // Ensure we don't leave things running if component unmounts or props change
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRecording, startVisualizer]);

  // Resume audio if needed and ensure visualize loop running when recording
  useEffect(() => {
    if (isRecording && initializedRef.current && analyserRef.current) {
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        audioContextRef.current.resume().catch((error) => {
          console.error("Error resuming audio context:", error);
        });
      }

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(visualize);
      }
    } else if (!isRecording && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [isRecording, visualize]);

  // Resize observer + resize handling
  useLayoutEffect(() => {
    // initial sizing
    resizeCanvasToContainer();

    const container = containerRef.current;
    if (!container) return;

    // ResizeObserver for container changes
    if (typeof ResizeObserver !== "undefined") {
      resizeObsRef.current = new ResizeObserver(() => {
        resizeCanvasToContainer();
      });
      resizeObsRef.current.observe(container);
    }

    const onWindowResize = () => {
      resizeCanvasToContainer();
    };

    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("resize", onWindowResize);
      if (resizeObsRef.current) {
        resizeObsRef.current.disconnect();
        resizeObsRef.current = null;
      }
    };
  }, [resizeCanvasToContainer]);

  // Clean up all resources on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch((error) => {
          console.error("Error closing audio context during unmount:", error);
        });
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      freqsRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  const wrapperWidth =
    typeof width === "number"
      ? `${width}px`
      : width ?? DEFAULT_WRAPPER_WIDTH;
  const wrapperHeight =
    typeof height === "number" ? `${height}px` : height ?? `${DEFAULT_HEIGHT}px`;

  return (
    <div
      ref={containerRef}
      style={{
        width: wrapperWidth,
        height: wrapperHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: `${MIN_CANVAS_WIDTH}px`,
        flex: 1,
      }}
    >
      <canvas
        ref={canvasRef}
        // Do NOT pass width/height attributes as calc strings.
        // We control the internal pixel buffer via resizeCanvasToContainer.
        style={{
          width: "100%",
          height: `${DEFAULT_HEIGHT}px`,
          maxHeight: wrapperHeight,
          display: "block",
        }}
      />
    </div>
  );
};

export default VoiceWaveVisualizer;
export { VoiceWaveVisualizer };
